import httpService from '@/modules/common/services/httpService';

const basePlatformRoute = '/platform/v1/scheduling';
const MAX_UIDS_PER_REQUEST = 30;

const fetchAppointmentsBatch = async (uidsChunk, matterUid, clientUid, perPage) => {
  let response;

  try {
    response = await httpService.get(`${basePlatformRoute}/appointments`, {
      uids: uidsChunk.join(','),
      matter_uid: matterUid,
      client_id: clientUid,
      per_page: perPage,
    });
  } catch (err) {
    console.error("Network/transport error while fetching appointments:", err);
    throw err;
  }

  const payload = response?.data;

  //  Missing payload — unexpected server failure (HTML error, empty body, etc.)
  if (!payload) {
    console.error("Invalid response: missing payload. Full response:", response);
    throw new Error("No response data received from the server");
  }

  const { status, error, errorCode, data } = payload;

  //  API explicitly returned an error (business logic, validation, rate limit)
  if (status !== "OK") {
    console.warn("Appointments API returned an error:", { error, errorCode });

    if (errorCode === "APPOINTMENTS_LIMIT_EXCEEDED") {
      throw new Error("Rate limit exceeded – please try again later");
    }

    throw new Error(error || "Server returned an unknown error");
  }

  //  Successful response — now validate structure

  if (!data) {
    console.warn("ℹ data is missing → returning []");
    return [];
  }

  if (!Array.isArray(data.appointments)) {
    console.warn("ℹ appointments is not array → returning []");
    return [];
  }

  return data.appointments;
};

const fetchAppointments = async (uids, matterUid, clientUid, perPage = 25) => {
  // Input validation
  if (!Array.isArray(uids) || uids.length === 0) {
    throw new Error('uids must be a non-empty array');
  }

  if (!clientUid) {
    throw new Error('clientUid is required');
  }

  try {
    if (uids.length <= MAX_UIDS_PER_REQUEST) {
      return await fetchAppointmentsBatch(uids, matterUid, clientUid, perPage);
    }

    const chunks = [];
    for (let i = 0; i < uids.length; i += MAX_UIDS_PER_REQUEST) {
      chunks.push(uids.slice(i, i + MAX_UIDS_PER_REQUEST));
    }

    const results = await Promise.all(
      chunks.map((chunk) => fetchAppointmentsBatch(chunk, matterUid, clientUid, perPage))
    );
    return results.flat();
  } catch (error) {
    console.error('Error fetching appointments:', error);
    throw error; // Re-throw for caller to handle
  }
};

export default {
  fetchAppointments,
};
